import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getUser,
  updateUserPermissions,
  updateUser,
  setUserPassword,
} from "../../../../api/userApi";
import { resendVerificationEmail } from "../../../../api/authApi";
import { authStore } from "../../../../stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "../../../../stores/permissionStore";
import { useSnapshot } from "valtio";
import { Button } from "../../../../components/ui/button";
import {
  ArrowLeft,
  Save,
  AlertCircle,
  KeyRound,
  MailCheck,
  MailWarning,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { toast } from "sonner";
import { GENDERS, USER_ROLES } from "../../../../utils/constants";
import { RankSelect } from "../../../../components/ui/rank-select";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "../../../../components/ui/table";

// Role levels, mirroring ROLE_LEVELS on the backend. Used to decide which
// roles the signed-in admin is allowed to assign.
const ROLE_LEVELS = { SEAFARER: 1, ADMIN: 2, SUPER_ADMIN: 3 };

const MIN_PASSWORD_LENGTH = 8;

// The editable profile fields, in render order. `type` drives which control is
// rendered; `options` is only read for selects.
const DETAIL_FIELDS = [
  { name: "name", label: "Full Name", type: "text" },
  { name: "email", label: "Email", type: "email" },
  { name: "indianCdc", label: "INDoS Number", type: "text" },
  { name: "mobileNumber", label: "Mobile Number", type: "text" },
  { name: "age", label: "Age", type: "number" },
  { name: "gender", label: "Gender", type: "select", options: GENDERS },
];

const ACTIONS = Object.values(PermissionAction);
const RESOURCES = Object.values(ResourceType);

// Resources that should be displayed in the permissions table
const DISPLAY_RESOURCES = RESOURCES.filter(
  (resource) => !["TOPIC", "LEARNING_MATERIAL", "TEST"].includes(resource)
);

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const currentUser = snap?.user;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [details, setDetails] = useState({});
  const [savingDetails, setSavingDetails] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
  const canUpdateUser = permSnap.can(PermissionAction.UPDATE, ResourceType.USER);

  // Check if user is trying to edit their own record. The backend refuses
  // self-edits on these routes — details, password and permissions all go
  // through your own profile instead — so the UI disables them rather than
  // letting the request fail.
  const isEditingOwnPermissions = currentUser?.id === id;
  const isEditingSelf = isEditingOwnPermissions;

  // An admin can only act on someone strictly below their own role level;
  // this mirrors checkAccess() on the backend.
  const outranksTarget =
    !!currentUser?.role &&
    !!userData?.role &&
    ROLE_LEVELS[currentUser.role] > ROLE_LEVELS[userData.role];

  const canEditDetails = canUpdateUser && !isEditingSelf && outranksTarget;

  // Only a super admin may change a role, and never to one at or above their
  // own level.
  const assignableRoles = isSuperAdmin
    ? USER_ROLES.filter(
        (role) => ROLE_LEVELS[role.value] < ROLE_LEVELS[currentUser.role]
      )
    : [];

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getUser(id);

        if (response && response.success && response.data) {
          const user = response.data;
          setUserData(user);
          setDetails(
            DETAIL_FIELDS.reduce(
              (acc, field) => ({ ...acc, [field.name]: user[field.name] ?? "" }),
              {
                role: user.role ?? "",
                rankDesignation: user.rankDesignation ?? "",
              }
            )
          );

          // Parse permissions
          let userPermissions = user.permissions;
          if (typeof userPermissions === "string") {
            try {
              userPermissions = JSON.parse(userPermissions);
            } catch {
              userPermissions = {};
            }
          }
          setPermissions(userPermissions || {});
        } else {
          const errorMessage =
            response?.message || "Failed to fetch user details";
          setError(errorMessage);
          toast.error(errorMessage);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Error fetching user details";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // Helper function to automatically add related resources when COURSE is selected
  const handlePermissionChange = (action, resource, checked) => {
    const updated = { ...permissions };

    if (!updated[action]) updated[action] = [];

    if (checked) {
      // Add the selected resource
      updated[action] = Array.from(new Set([...updated[action], resource]));

      // If COURSE is selected, automatically add TOPIC, LEARNING_MATERIAL, and TEST
      if (resource === "COURSE") {
        updated[action] = Array.from(
          new Set([...updated[action], "TOPIC", "LEARNING_MATERIAL", "TEST"])
        );
      }
    } else {
      // Remove the resource
      updated[action] = updated[action].filter((r) => r !== resource);

      // If COURSE is unselected, also remove TOPIC, LEARNING_MATERIAL, and TEST
      if (resource === "COURSE") {
        updated[action] = updated[action].filter(
          (r) => !["TOPIC", "LEARNING_MATERIAL", "TEST"].includes(r)
        );
      }
    }

    // Clean up invalid permissions based on backend validation
    const validPermissions = {
      CREATE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "SUPPORT_TICKET",
        "USER",
        "ALL",
      ],
      READ: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "ANALYTICS",
        "SUPPORT_TICKET",
        "ALL",
      ],
      UPDATE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "SUPPORT_TICKET",
        "ALL",
      ],
      DELETE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "SUPPORT_TICKET",
        "ALL",
      ],
      EXPORT: ["ANALYTICS", "ALL"],
      NOTIFICATION: ["USER", "ALL"],
    };

    // Filter out invalid permissions
    if (validPermissions[action]) {
      updated[action] = updated[action].filter((r) =>
        validPermissions[action].includes(r)
      );
    }

    setPermissions(updated);
  };

  const toastText = (value, fallback) => {
    if (Array.isArray(value)) {
      const joined = value.filter(Boolean).join(" ");
      return joined || fallback;
    }
    if (typeof value === "string" && value.trim()) return value.trim();
    return fallback;
  };

  const handleSendVerification = async () => {
    if (!userData?.email) {
      toast.error("This user has no email address on file");
      return;
    }

    const pendingEmail = (details.email ?? "").trim().toLowerCase();
    const savedEmail = String(userData.email).trim().toLowerCase();
    if (pendingEmail !== savedEmail) {
      toast.error("Save the new email before sending the verification email");
      return;
    }

    setSendingVerification(true);
    try {
      const response = await resendVerificationEmail(userData.email);
      toast.success(
        toastText(
          response?.message,
          `Verification email sent to ${userData.email}`
        )
      );
    } catch (err) {
      const raw = err?.message || err?.response?.data?.message;
      toast.error(toastText(raw, "Failed to send verification email"));
    } finally {
      setSendingVerification(false);
    }
  };

  const handleDetailChange = (name, value) => {
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Only send what actually changed. The backend rejects an empty patch, which
  // is what tells the admin they pressed Save without editing anything.
  const changedDetails = () => {
    const payload = {};

    [...DETAIL_FIELDS.map((f) => f.name), "rankDesignation", "role"].forEach(
      (name) => {
        const next = details[name];
        const current = userData?.[name] ?? "";

        if (String(next ?? "") === String(current ?? "")) return;

        if (name === "age") {
          payload.age = next === "" ? null : Number(next);
          return;
        }
        payload[name] = next === "" ? null : next;
      }
    );

    return payload;
  };

  const handleSaveDetails = async () => {
    if (!canEditDetails) {
      toast.error("You don't have permission to edit this user's details");
      return;
    }

    const payload = changedDetails();
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSavingDetails(true);
    try {
      const response = await updateUser(id, payload);

      if (response?.success === false) {
        throw new Error(response.message || "Failed to update user");
      }

      // Re-seed from the server's copy so normalised values (a lowercased
      // email, a trimmed INDoS number) are what the form now shows — otherwise
      // the next Save would see the un-normalised text as another change.
      const updated = response?.user ?? response?.data ?? null;
      if (updated) {
        setUserData(updated);
        setDetails(
          DETAIL_FIELDS.reduce(
            (acc, field) => ({ ...acc, [field.name]: updated[field.name] ?? "" }),
            {
              role: updated.role ?? "",
              rankDesignation: updated.rankDesignation ?? "",
            }
          )
        );
      }
      toast.success("User details updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update user details");
    } finally {
      setSavingDetails(false);
    }
  };

  const handleSavePassword = async () => {
    if (!canEditDetails) {
      toast.error("You don't have permission to change this user's password");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
      );
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSavingPassword(true);
    try {
      const response = await setUserPassword(id, password);

      if (response?.success === false) {
        throw new Error(response.message || "Failed to update password");
      }

      setPassword("");
      setConfirmPassword("");
      toast.success("Password updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSave = async () => {
    if (!canUpdateUser) {
      toast.error("You don't have permission to update users");
      return;
    }

    // Prevent users from editing their own permissions
    if (isEditingOwnPermissions) {
      toast.error("You cannot edit your own permissions");
      return;
    }

    setSaving(true);
    try {
      console.log("Updating permissions:", permissions);

      const response = await updateUserPermissions(id, {
        permissions: permissions,
      });

      if (
        response.success ||
        response.message === "User updated successfully"
      ) {
        toast.success("User permissions updated successfully");
        navigate(`/admin/users/view/${id}`);
      } else {
        throw new Error(response.message || "Failed to update permissions");
      }
    } catch (err) {
      console.error("Error updating permissions:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update permissions";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 text-red-500">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-4">
        <div className="text-center text-gray-500">No user data found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-6 w-full">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancel
          </Button>
          {canUpdateUser && !isEditingOwnPermissions && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#5D93A5] text-white"
            >
              {saving ? (
                "Saving..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Permissions
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <Card className="w-full">
        <CardHeader>
          <CardTitle>Edit User - {userData.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Editable user details */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">User Information</h3>
              {canEditDetails && (
                <Button
                  onClick={handleSaveDetails}
                  disabled={savingDetails}
                  className="bg-[#5D93A5] text-white"
                >
                  {savingDetails ? (
                    "Saving..."
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Details
                    </>
                  )}
                </Button>
              )}
            </div>

            {!canEditDetails && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong>{" "}
                  {isEditingSelf
                    ? "You cannot edit your own details here — use your profile page."
                    : !outranksTarget
                      ? `You cannot edit a ${userData.role} because your role (${currentUser?.role}) is not above theirs.`
                      : "You don't have permission to edit user details."}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {DETAIL_FIELDS.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>{field.label}</Label>
                  {field.type === "select" ? (
                    <Select
                      value={details[field.name] || ""}
                      onValueChange={(value) =>
                        handleDetailChange(field.name, value)
                      }
                      disabled={!canEditDetails || savingDetails}
                    >
                      <SelectTrigger id={field.name}>
                        <SelectValue placeholder={`Select ${field.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={field.name}
                      type={field.type}
                      value={details[field.name] ?? ""}
                      onChange={(e) =>
                        handleDetailChange(field.name, e.target.value)
                      }
                      disabled={!canEditDetails || savingDetails}
                    />
                  )}
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="rankDesignation">Rank / Designation</Label>
                <RankSelect
                  id="rankDesignation"
                  value={details.rankDesignation ?? ""}
                  onValueChange={(value) =>
                    handleDetailChange("rankDesignation", value)
                  }
                  placeholder="Search and select rank…"
                  disabled={!canEditDetails || savingDetails}
                  allowOther
                />
              </div>

              {/* Role is super-admin only, and never assignable at or above
                  the signed-in admin's own level. */}
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                {isSuperAdmin && canEditDetails ? (
                  <Select
                    value={details.role || ""}
                    onValueChange={(value) => handleDetailChange("role", value)}
                    disabled={savingDetails}
                  >
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="role"
                    value={userData.role || "N/A"}
                    disabled
                    readOnly
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Input
                  id="status"
                  value={userData.ban ? "Banned" : "Active"}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Email verification */}
          <div className="rounded-lg border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2">
                {userData.emailVerified ? (
                  <MailCheck className="h-4 w-4 mt-0.5 text-green-600" />
                ) : (
                  <MailWarning className="h-4 w-4 mt-0.5 text-amber-600" />
                )}
                <div>
                  <h3 className="text-lg font-semibold leading-none mb-1">
                    Email Verification
                  </h3>
                  <p className="text-sm text-gray-500">
                    {userData.emailVerified
                      ? `${userData.email} is verified.`
                      : `${userData.email} has not been verified yet. Sending a verification email lets the user confirm it themselves.`}
                  </p>
                </div>
              </div>

              {!userData.emailVerified && (
                <Button
                  variant="outline"
                  onClick={handleSendVerification}
                  disabled={sendingVerification}
                >
                  {sendingVerification ? (
                    "Sending..."
                  ) : (
                    <>
                      <MailCheck className="mr-2 h-4 w-4" />
                      Send Verification Email
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Password */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-4 w-4 text-gray-600" />
              <h3 className="text-lg font-semibold">Password</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Sets the user&apos;s password immediately and clears any login
              lockout. They are not notified — tell them the new password
              yourself.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!canEditDetails || savingPassword}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={!canEditDetails || savingPassword}
                />
              </div>
              <div>
                <Button
                  variant="outline"
                  onClick={handleSavePassword}
                  disabled={
                    !canEditDetails ||
                    savingPassword ||
                    !password ||
                    !confirmPassword
                  }
                >
                  {savingPassword ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </div>
          </div>

          {/* Permissions Section */}
          {canUpdateUser ? (
            <div className="rounded-lg border p-4">
              {isEditingOwnPermissions && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> You cannot edit your own permissions. Please ask another administrator to update your permissions.
                  </p>
                </div>
              )}
              <Label className="block mb-4 text-lg font-semibold">
                Edit User Permissions
              </Label>
              <div className="overflow-x-auto">
                <Table className="min-w-full border text-sm text-left">
                  <TableHeader>
                    <TableRow className="py-2">
                      <TableHead className="border px-3 py-2 bg-gray-50 font-semibold">
                        Action
                      </TableHead>
                      {DISPLAY_RESOURCES.map((resource) => (
                        <TableHead
                          key={resource}
                          className="border px-3 py-2 bg-gray-50 font-semibold text-center"
                        >
                          {resource.replace(/_/g, " ")}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ACTIONS.map((action) => (
                      <TableRow key={action}>
                        <TableCell className="border px-3 py-2 font-medium bg-gray-25">
                          {action}
                        </TableCell>
                        {DISPLAY_RESOURCES.map((resource) => {
                          const checked = (permissions[action] || []).includes(
                            resource
                          );
                          const isDisabled =
                            saving ||
                            (action === "NOTIFICATION" &&
                              resource !== "USER") ||
                            (action === "EXPORT" && resource !== "ANALYTICS") ||
                            (action === "CREATE" && resource === "ANALYTICS") ||
                            (action === "UPDATE" && resource === "ANALYTICS") ||
                            (action === "DELETE" && resource === "ANALYTICS");

                          return (
                            <TableCell
                              key={resource}
                              className="border px-3 py-2 text-center"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                className="w-4 h-4 border-gray-300 rounded accent-[#5D93A5]"
                                disabled={isDisabled || isEditingOwnPermissions}
                                onChange={(e) => {
                                  if (isEditingOwnPermissions) {
                                    toast.error("You cannot edit your own permissions");
                                    return;
                                  }
                                  handlePermissionChange(
                                    action,
                                    resource,
                                    e.target.checked
                                  );
                                }}
                                aria-label={`${action} ${resource}`}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> You don't have permission to modify user
                permissions.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
